"use client";

import React, { useState, useEffect } from "react";
import { MapPin, Clock, Phone, Mail, Globe, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SHARED_DATA = {
  address: "1234 Innovation Drive, Tech City, TC 56789",
  phone: "+1 (800) 555-0199",
  email: "contact@mollycall.ai",
  locations: [{
    id: "hq",
    city: "Tech City",
    country: "USA",
    timezone: "America/New_York",
    address: "1234 Innovation Drive, Tech City, TC 56789",
    phone: "+1 (800) 555-0199",
    email: "hq@mollycall.ai",
    coords: {
      top: "32%",
      left: "26%"
    }
  }, {
    id: "uk",
    city: "London",
    country: "UK",
    timezone: "Europe/London",
    address: "78 AI Boulevard, Shoreditch, London, E1 6JJ",
    phone: "+44 20 7123 4567",
    email: "london@mollycall.ai",
    coords: {
      top: "24%",
      left: "49%"
    }
  }, {
    id: "au",
    city: "Sydney",
    country: "Australia",
    timezone: "Australia/Sydney",
    address: "Level 45, 100 Barangaroo Ave, Sydney NSW 2000",
    phone: "+61 2 9876 5432",
    email: "sydney@mollycall.ai",
    coords: {
      top: "78%",
      left: "88%"
    }
  }]
};
export default function OfficeGlobalPresence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [currentTime, setCurrentTime] = useState<Record<string, string>>({});
  useEffect(() => {
    const updateTimes = () => {
      const times: Record<string, string> = {};
      SHARED_DATA.locations.forEach(loc => {
        try {
          times[loc.id] = new Intl.DateTimeFormat("en-US", {
            timeZone: loc.timezone,
            hour: "numeric",
            minute: "numeric",
            hour12: true
          }).format(new Date());
        } catch (e) {
          times[loc.id] = "N/A";
        }
      });
      setCurrentTime(times);
    };
    updateTimes();
    const interval = setInterval(updateTimes, 60000);
    return () => clearInterval(interval);
  }, []);
  return <section id="office-global-presence" className="relative w-full py-24 overflow-hidden bg-[hsl(240,4%,7%)] text-white">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-[hsl(221,83%,53%)]/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-[hsl(221,83%,53%)]/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 font-sans">
            Global <span className="text-[hsl(221,83%,53%)]">Presence</span>
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed">
            Strategically located hubs ensuring 24/7 support and enterprise-grade reliability for our partners worldwide.
          </p>
        </div>

        {}
        <div className="relative w-full aspect-[16/9] md:aspect-[2/1] bg-[hsl(240,4%,10%)]/50 rounded-2xl border border-white/5 shadow-2xl overflow-hidden backdrop-blur-sm">
          {}
          <div className="absolute inset-0 opacity-40">
             <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/abstract_world_map_dots.png" alt="World Map" className="w-full h-full object-cover object-center opacity-60 mix-blend-screen" />
             {}
             <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px]"></div>
          </div>

          {}
          {SHARED_DATA.locations.map((loc, i) => <div key={loc.id} data-index={i} className="absolute transform -translate-x-1/2 -translate-y-1/2 group" style={{
          top: loc.coords.top,
          left: loc.coords.left
        }}>
              <HoverCard openDelay={0} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div role="button" tabIndex={0} className="flex items-center gap-3 focus:outline-none group cursor-pointer">
                    {}
                    <div className="relative">
                      <div className="absolute -inset-2 bg-[hsl(221,83%,53%)]/30 rounded-full animate-ping opacity-75"></div>
                      <div className="relative w-3 h-3 bg-[hsl(221,83%,53%)] rounded-full shadow-[0_0_10px_hsl(221,83%,53%)] border border-white/20 group-hover:scale-125 transition-transform duration-300"></div>
                    </div>
                    
                    {}
                    <span className="hidden md:block text-sm font-medium text-gray-300 bg-black/50 px-2 py-1 rounded backdrop-blur-md border border-white/10 group-hover:text-white group-hover:border-[hsl(221,83%,53%)]/50 transition-colors">
                      {loc.city}
                    </span>
                  </div>
                </HoverCardTrigger>
                
                <HoverCardContent side="top" align="center" className="w-80 p-0 border-white/10 bg-[hsl(240,4%,10%)]/95 backdrop-blur-xl text-white shadow-2xl overflow-hidden">
                  {}
                  <div className="bg-[hsl(221,83%,53%)]/10 p-4 border-b border-white/5 flex justify-between items-center">
                    <div>
                        <h4 className="font-bold text-lg text-white flex items-center gap-2">
                            <Globe className="w-4 h-4 text-[hsl(221,83%,53%)]" />
                            {loc.city}, {loc.country}
                        </h4>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-mono text-[hsl(221,83%,53%)] bg-[hsl(221,83%,53%)]/10 px-2 py-1 rounded">
                        <Clock className="w-3 h-3" />
                        {currentTime[loc.id] || "Loading..."}
                    </div>
                  </div>

                  {}
                  <div className="p-4 space-y-4">
                    <div className="space-y-3">
                        <div className="flex items-start gap-3 text-sm text-gray-400">
                            <MapPin className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
                            <span>{loc.address}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                            <Phone className="w-4 h-4 text-gray-500 shrink-0" />
                            <span className="hover:text-white transition-colors">{loc.phone}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                            <Mail className="w-4 h-4 text-gray-500 shrink-0" />
                            <span className="hover:text-white transition-colors">{loc.email}</span>
                        </div>
                    </div>

                    <div className="pt-2">
                        <Link to="/ContactUsPage" className="w-full block">
                            <Button variant="outline" size="sm" className="w-full border-white/10 hover:bg-white/5 hover:text-white text-gray-300 justify-between group/btn">
                                Get in touch
                                <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                            </Button>
                        </Link>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>)}
        </div>

        {}
        <div className="mt-12 grid grid-cols-1 gap-6 md:hidden">
            {SHARED_DATA.locations.map((loc, i) => <div key={`mobile-${loc.id}`} data-index={i} className="bg-[hsl(240,4%,10%)] border border-white/10 rounded-xl p-6">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h3 className="text-xl font-bold text-white">{loc.city}</h3>
                            <p className="text-sm text-[hsl(221,83%,53%)]">{loc.country}</p>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs font-mono text-gray-400 bg-white/5 px-2 py-1 rounded">
                            <Clock className="w-3 h-3" />
                            {currentTime[loc.id] || "--:--"}
                        </div>
                    </div>
                    <div className="space-y-3 mb-6">
                        <p className="text-sm text-gray-400 flex items-start gap-2">
                            <MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {loc.address}
                        </p>
                        <p className="text-sm text-gray-400 flex items-center gap-2">
                            <Phone className="w-4 h-4 shrink-0" /> {loc.phone}
                        </p>
                        <p className="text-sm text-gray-400 flex items-center gap-2">
                            <Mail className="w-4 h-4 shrink-0" /> {loc.email}
                        </p>
                    </div>
                    <Link to="/ContactUsPage" className="w-full block">
                        <Button className="w-full bg-[hsl(221,83%,53%)] hover:bg-[hsl(221,83%,53%)]/90 text-white">
                            Contact Office
                        </Button>
                    </Link>
                </div>)}
        </div>
      </div>
    </section>;
}